// 로그인 이메일 자동완성(2026-09-28 사용자 요청) — 기억 목록 + 추천 계산. 화면은 `views/EmailSuggestField.tsx`.
//
// - **로그인에 성공했을 때만** 기억한다(App 의 로그인 성공 지점) — 오타·틀린 이메일이 목록에 쌓이지 않게.
// - 이메일은 비밀이 아니므로 localStorage(이 뷰어/앱에만). 🔴 **비밀번호는 어디에도 저장하지 않는다.**
// - 추천 규칙(`suggestEmails`):
//     ① 기억된 이메일 중 **입력한 글자로 시작하는 것**(최근 순). 칸이 비었거나 입력이 기억된 이메일과 똑같으면
//        (= 아직 새로 치지 않았다 — 계정 전환하려고 칸을 누른 경우) 기억된 전부.
//     ② `@` 뒤를 치고 있으면 **도메인 제안** — 기억된 이메일의 도메인을 먼저, 그다음 흔한 도메인(`COMMON_EMAIL_DOMAINS`).
//        ①에 이미 나온 이메일은 다시 넣지 않는다.
// - 저장 형식이 바뀌기 전(2026-09-27)에는 마지막 1개만 `rs.lastLoginId` 에 두었다 → 목록이 비어 있으면 그것을 이어받는다.

/** localStorage 키 — 최근 로그인 이메일 목록(최신 먼저, JSON 배열). */
export const LOGIN_EMAILS_KEY = 'rs.loginEmails';
/** 이전 형식(마지막 1개) 키 — 이어받기용. */
export const LEGACY_LAST_LOGIN_ID_KEY = 'rs.lastLoginId';
/** 기억하는 최대 개수(오래된 것부터 버린다). */
export const LOGIN_EMAILS_MAX = 10;
/** 도메인 제안 최대 개수(목록이 창을 덮지 않게). */
export const DOMAIN_SUGGEST_MAX = 5;

/** `@` 뒤 도메인 제안에 쓰는 흔한 도메인(국내 우선). 순서 = 제안 순서. */
export const COMMON_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'naver.com',
  'daum.net',
  'hanmail.net',
  'kakao.com',
  'nate.com',
  'outlook.com',
  'hotmail.com',
  'icloud.com',
];

/** 추천 항목 종류 — saved = 전에 로그인한 이메일(× 로 지울 수 있다), domain = 도메인 채워 주기. */
export type EmailSuggestionKind = 'saved' | 'domain';

export interface EmailSuggestion {
  /** 고르면 칸에 들어갈 이메일 전체. */
  value: string;
  kind: EmailSuggestionKind;
}

/** 저장소(시험에서 바꿔 끼운다). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** 이메일 정리 — 앞뒤 공백 제거 + 소문자(서버도 소문자로 저장·비교한다, §14.19). */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** 최근 로그인 이메일(최신 먼저). 저장 값이 깨졌거나 저장소가 막혔으면 빈 목록. */
export function loadLoginEmails(store: KeyValueStore | null = defaultStore()): string[] {
  try {
    const raw = store?.getItem(LOGIN_EMAILS_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(v) ? v.filter((e): e is string => typeof e === 'string' && e !== '') : [];
    if (list.length > 0) return list.slice(0, LOGIN_EMAILS_MAX);
    // 이전 형식 이어받기(읽기만 — 다음 로그인 성공 때 새 형식으로 저장된다).
    const legacy = normalizeEmail(store?.getItem(LEGACY_LAST_LOGIN_ID_KEY) ?? '');
    return legacy ? [legacy] : [];
  } catch {
    return [];
  }
}

function save(list: string[], store: KeyValueStore | null): void {
  try {
    store?.setItem(LOGIN_EMAILS_KEY, JSON.stringify(list.slice(0, LOGIN_EMAILS_MAX)));
  } catch {
    // 저장이 막힌 환경 — 자동완성만 빠진다.
  }
}

/** 로그인 성공 — 맨 앞으로(이미 있으면 옮긴다). 갱신된 목록을 돌려준다. */
export function rememberLoginEmail(email: string, store: KeyValueStore | null = defaultStore()): string[] {
  const e = normalizeEmail(email);
  if (!e) return loadLoginEmails(store);
  const list = [e, ...loadLoginEmails(store).filter((x) => x !== e)].slice(0, LOGIN_EMAILS_MAX);
  save(list, store);
  return list;
}

/** 목록에서 지우기(사람이 × 를 눌렀을 때). 갱신된 목록을 돌려준다. */
export function forgetLoginEmail(email: string, store: KeyValueStore | null = defaultStore()): string[] {
  const e = normalizeEmail(email);
  const list = loadLoginEmails(store).filter((x) => x !== e);
  save(list, store);
  return list;
}

/**
 * 입력값에 맞는 추천 목록(위 규칙 ①②). 순수 함수 — 화면과 시험이 같은 것을 쓴다.
 * @param input 칸에 지금 들어 있는 값(정리 전).
 * @param saved 기억된 이메일(최신 먼저, `loadLoginEmails` 결과).
 * @param domains 흔한 도메인(기본 `COMMON_EMAIL_DOMAINS`).
 */
export function suggestEmails(
  input: string,
  saved: readonly string[],
  domains: readonly string[] = COMMON_EMAIL_DOMAINS,
): EmailSuggestion[] {
  const q = normalizeEmail(input);

  // ① 기억된 이메일 — 비었거나 이미 기억된 것과 똑같으면 전부(계정 전환), 아니면 앞부분이 같은 것만.
  const showAll = q === '' || saved.includes(q);
  const savedHits = showAll ? [...saved] : saved.filter((e) => e.startsWith(q));
  const out: EmailSuggestion[] = savedHits.map((value) => ({ value, kind: 'saved' }));

  // ② `@` 뒤 도메인 제안 — `@` 앞이 있어야 하고, 도메인을 이미 끝까지 친 경우는 제안하지 않는다.
  const at = q.indexOf('@');
  if (at > 0 && q.indexOf('@', at + 1) < 0) {
    const local = q.slice(0, at);
    const typed = q.slice(at + 1);
    const savedDomains = saved.map((e) => e.slice(e.indexOf('@') + 1)).filter((d) => d !== '');
    const seen = new Set(out.map((s) => s.value));
    let n = 0;
    for (const d of new Set([...savedDomains, ...domains])) {
      if (n >= DOMAIN_SUGGEST_MAX) break;
      if (!d.startsWith(typed) || d === typed) continue;
      const value = `${local}@${d}`;
      if (seen.has(value)) continue;
      seen.add(value);
      out.push({ value, kind: 'domain' });
      n++;
    }
  }
  return out;
}
